import React, { useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { HOADONTHANHTOAN_API } from '@app/utils/apiUrl';
import { Get, Put } from '@app/utils/httpProvider';
import { useQuery, useQueryClient } from 'react-query';
import DatePicker from 'react-datepicker';
import { DATE_TIME_REACT_WITHOUT_HOUR_FORMAT } from '@app/utils/define';

const validationSchema = yup.object({
    cusTaxCode: yup.string().trim().required('Vui lòng nhập mã số thuế'),
    cusCompanyName: yup.string().trim().required('Bấm kính lúp để tra cứu mã số thuế'),
    cusAddress: yup.string().trim().required('Bấm kính lúp để tra cứu mã số thuế'),
    cusEmail: yup.string().trim()
        .required('Vui lòng nhập email')
        .email('Email không đúng định dạng'),
    mdvqhnSach: yup.string().trim()
        .matches(/^\d{7}$/, {
            message: 'Mã ĐVQHNS phải gồm đúng 7 chữ số',
            excludeEmptyString: true
        })
});

const requiredFields = new Set(['cusTaxCode', 'cusCompanyName', 'cusAddress', 'cusEmail']);

const modalConfig = {
    edit: ['Cập nhật thông tin xuất hóa đơn', 'Cập nhật thông tin'],
    replace: ['Chỉnh sửa thông tinh', 'Yêu cầu chỉnh sửa'],
    adjust: ['Thông tin yêu cầu hoàn tiền', 'Yêu cầu hoàn tiền']
};

const invoiceStatus = {
    pendingIssue: 0,
    issued: 1
};

const statusByMode = {
    edit: invoiceStatus.pendingIssue,
    replace: invoiceStatus.issued,
    adjust: invoiceStatus.issued
};

const pascalName = (key) => key === 'erpid' ? 'ERPID'
    : key === 'newERPID' ? 'NewERPID'
        : key === 'mdvqhnSach' ? 'MDVQHNSach'
            : key.charAt(0).toUpperCase() + key.slice(1);

const valueOf = (source, key) => source?.[key] ?? source?.[pascalName(key)];

const COMPANY_INFO_API = 'https://hoadon.benhvien108.vn/api/business/get_company_info';

const XuatHoaDonModal = ({
    show,
    onHide,
    onSuccess,
    app,
    payment,
    dotThanhToan,
    erpId,
    mode = 'edit',
    notification
}) => {
    const [isLookingUpTaxCode, setIsLookingUpTaxCode] = useState(false);
    const soHoSo = app?.soHoSo ?? app?.SoHoSo ?? '';
    const paymentId = payment?.id ?? payment?.Id ?? '';
    const getDotThanhToan = soHoSo[2] != '1' ? dotThanhToan : '';
    const custCode = `${soHoSo}${getDotThanhToan}`;
    const invoiceQueryErpId = erpId || custCode;
    const currentMode = modalConfig[mode] ? mode : 'edit';
    const currentStatus = statusByMode[currentMode];
    const [modalTitle, submitText] = modalConfig[currentMode];
    const queryClient = useQueryClient();
    const itemQuery = useQuery(
        ['xuatHoaDonData', invoiceQueryErpId, currentStatus],
        () => Get(
            HOADONTHANHTOAN_API.GET_ITEM_BY_ERPID_AND_STATUS,
            `${encodeURIComponent(invoiceQueryErpId)}&${currentStatus}`
        ),
        { enabled: show && !!invoiceQueryErpId }
    );

    console.log('XuatHoaDonModal - itemQuery:', itemQuery);
    const existingInvoice = itemQuery.data?.data ?? itemQuery.data;
    const defaultValues = {
        erpid: '', newERPID: '', serial: '', pattern: '',
        arisingDate: new Date(),
        cusName: app?.hoTen ?? app?.HoTen ?? '',
        cusCompanyName: '', cusEmail: app?.email ?? app?.Email ?? '',
        cusAddress: app?.thuongTruDiaChi ?? app?.ThuongTruDiaChi ?? '',
        cusBankNo: '', cusBankName: '', cusFax: '',
        cusPhone: app?.soDienThoai ?? app?.SoDienThoai ?? '',
        cusTaxCode: '', paymentMethod: 'Chuyển khoản',
        createBy: app?.hoTen ?? app?.HoTen ?? '', soPT: custCode,
        soCCCDan: app?.soCCCD ?? app?.SoCCCD ?? '', mdvqhnSach: '', note: '',
    };
    const initialValues = !existingInvoice
        ? defaultValues
        : Object.keys(defaultValues).reduce((result, key) => ({
            ...result,
            [key]: key === 'arisingDate'
                ? new Date(valueOf(existingInvoice, key) ?? result[key])
                : valueOf(existingInvoice, key) ?? result[key]
        }), defaultValues);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const products = valueOf(existingInvoice, 'products');
            const currentErpId = valueOf(existingInvoice, 'erpid');
            const lichSuThanhToanId = valueOf(existingInvoice, 'lichSuThanhToanId');
            const invoiceNo = valueOf(existingInvoice, 'invoiceNo');
            const invoiceType = valueOf(existingInvoice, 'invoiceType');
            console.log('Submitting invoice data:', values);
            const payload = {
                ...values,
                CustCode: custCode,
                cusCode: soHoSo,
                lichSuThanhToanId: lichSuThanhToanId,
                products: products?.$values ?? products ?? [],
                vatRate: valueOf(existingInvoice, 'vatRate') ?? 0,
                total: valueOf(existingInvoice, 'total') ?? 0,
                vatAmount: valueOf(existingInvoice, 'vatAmount') ?? 0,
                discountPercent: valueOf(existingInvoice, 'discountPercent') ?? 0,
                discountAmount: valueOf(existingInvoice, 'discountAmount') ?? 0,
                amount: valueOf(existingInvoice, 'amount') ?? 0,
                invoiceNo: invoiceNo ?? '',
                invoiceType: invoiceType ?? '',
                amountInWords: valueOf(existingInvoice, 'amountInWords') ?? ''
            };

            if (currentMode === 'replace') {
                if (!currentErpId) throw new Error('Không xác định được ERP ID của hóa đơn cần thay thế');
                await Put(
                    HOADONTHANHTOAN_API.REQUEST_REPLACE,
                    currentErpId,
                    payload
                );
            } else if (currentMode === 'adjust') {
                if (!currentErpId) throw new Error('Không xác định được ERP ID của hóa đơn cần điều chỉnh');
                await Put(
                    HOADONTHANHTOAN_API.REQUEST_ADJUST,
                    currentErpId,
                    payload
                );
            } else {
                const invoiceId = existingInvoice?.id ?? existingInvoice?.Id;
                await Put(HOADONTHANHTOAN_API.UPDATE_ITEM, invoiceId, payload);
            }

            toast.success(`${submitText} thành công`);
            await Promise.all([
                queryClient.invalidateQueries(['xuatHoaDonData', invoiceQueryErpId, currentStatus]),
                queryClient.invalidateQueries(['paymentHistoryForInvoicePreview', custCode])
            ]);
            await onSuccess?.();
            onHide();
        } catch (error) {
            toast.error(error?.message || error?.title || `Không thể ${submitText.toLowerCase()}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleLookupTaxCode = async (taxCode, setFieldValue) => {
        const normalizedTaxCode = taxCode?.trim();
        if (!normalizedTaxCode) {
            toast.error('Vui lòng nhập mã số thuế');
            return;
        }

        setIsLookingUpTaxCode(true);
        try {
            const response = await fetch(
                `${COMPANY_INFO_API}?mst=${encodeURIComponent(normalizedTaxCode)}`
            );
            if (!response.ok) throw new Error('Không thể tra cứu mã số thuế');

            const responseData = await response.json();
            const companyInfo = responseData?.data ?? responseData;
            if (!companyInfo?.name && !companyInfo?.address) {
                throw new Error('Không tìm thấy thông tin doanh nghiệp');
            }

            setFieldValue('cusCompanyName', companyInfo.name ?? '');
            setFieldValue('cusAddress', companyInfo.address ?? '');
            toast.success('Đã cập nhật thông tin doanh nghiệp');
        } catch (error) {
            toast.error(error?.message || 'Không thể tra cứu mã số thuế');
        } finally {
            setIsLookingUpTaxCode(false);
        }
    };

    const fields = [
        ['Mã số thuế', 'cusTaxCode'], ['Tên đơn vị', 'cusCompanyName'],
        ['Địa chỉ', 'cusAddress'], ['Email', 'cusEmail', 'email'],
        ['Số CCCD', 'soCCCDan'],
        ['Mã ĐVQHNS', 'mdvqhnSach'],
        ['Số tài khoản', 'cusBankNo'], ['Ngân hàng', 'cusBankName'],
        ['Điện thoại', 'cusPhone'],
        ['Fax', 'cusFax'],
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" style={{ zIndex: 10000 }}>
            <Formik enableReinitialize initialValues={initialValues}
                validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({
                    handleSubmit: submit,
                    handleChange,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    validateForm,
                    setTouched,
                    setFieldValue,
                    submitForm
                }) => (
                    <Form noValidate onSubmit={submit}>
                        <Modal.Header closeButton className="bg-primary text-white">
                            <Modal.Title><i className="fas fa-file-invoice mr-2" />{modalTitle}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {notification?.message && (
                                <div className="alert alert-success d-flex align-items-center" role="alert">
                                    <i className="fas fa-check-circle mr-2" />
                                    <span>{notification.message}</span>
                                </div>
                            )}
                            <Row>
                                <Col md={6}><Form.Group><Form.Label>Mã thanh toán</Form.Label>
                                    <Form.Control value={custCode} disabled /></Form.Group></Col>
                                <Col md={6}><Form.Group><Form.Label>Mã khách hàng</Form.Label>
                                    <Form.Control value={soHoSo} disabled /></Form.Group></Col>
                                {fields.map(([label, name, type = 'text']) => (
                                    <Col md={6} key={name}><Form.Group>
                                        <Form.Label>
                                            {label}
                                            {requiredFields.has(name) && <span className="text-danger ml-1">*</span>}
                                        </Form.Label>
                                        {name === 'arisingDate' ? (
                                            <div className="d-block">
                                                <DatePicker
                                                    selected={values.arisingDate}
                                                    onChange={(date) => setFieldValue('arisingDate', date)}
                                                    dateFormat={DATE_TIME_REACT_WITHOUT_HOUR_FORMAT}
                                                    className="form-control"
                                                    name="arisingDate"
                                                />
                                            </div>
                                        ) : name === 'cusTaxCode' ? (
                                            <>
                                                <InputGroup>
                                                    <Form.Control type={type} name={name} value={values[name] ?? ''}
                                                        onChange={handleChange} isInvalid={touched[name] && !!errors[name]} />
                                                    <InputGroup.Append>
                                                        <Button variant="outline-primary" type="button"
                                                            title="Tra cứu mã số thuế"
                                                            aria-label="Tra cứu mã số thuế"
                                                            disabled={isLookingUpTaxCode}
                                                            onClick={() => handleLookupTaxCode(values.cusTaxCode, setFieldValue)}>
                                                            <i className={`fas ${isLookingUpTaxCode ? 'fa-spinner fa-spin' : 'fa-search'}`} />
                                                        </Button>
                                                    </InputGroup.Append>
                                                    <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
                                                </InputGroup>
                                            </>
                                        ) : (
                                            <>
                                                <Form.Control type={type} name={name} value={values[name] ?? ''}
                                                    onChange={handleChange}
                                                    disabled={name === 'cusCompanyName' || name === 'cusAddress' || name === 'soCCCDan'}
                                                    isInvalid={touched[name] && !!errors[name]} />
                                                <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
                                            </>
                                        )}
                                    </Form.Group></Col>
                                ))}
                                <Col md={12}><Form.Group className="mb-0"><Form.Label>Ghi chú</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="note" value={values.note} onChange={handleChange} />
                                </Form.Group></Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-secondary" onClick={onHide} disabled={isSubmitting}>Hủy</Button>
                            <Button variant="success" type="button"
                                disabled={isSubmitting || itemQuery.isLoading || !custCode || !existingInvoice}
                                onClick={async () => {
                                    const validationErrors = await validateForm();
                                    const invalidFields = Object.keys(validationErrors);
                                    if (invalidFields.length > 0) {
                                        setTouched(invalidFields.reduce((result, field) => ({
                                            ...result,
                                            [field]: true
                                        }), {}));
                                        toast.error(validationErrors[invalidFields[0]]);
                                        window.setTimeout(() => {
                                            const firstInvalidInput = document.querySelector(
                                                `[name="${invalidFields[0]}"]`
                                            );
                                            firstInvalidInput?.focus();
                                            firstInvalidInput?.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center'
                                            });
                                        }, 0);
                                        return;
                                    }
                                    submitForm();
                                }}>
                                {(isSubmitting || itemQuery.isLoading) && <i className="fas fa-spinner fa-spin mr-2" />}
                                {submitText}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default XuatHoaDonModal;
